import React, { useMemo } from "react";
import { Col, Form, Modal, Row, Table } from "react-bootstrap";
import styled from "styled-components";
import { FiAlertCircle, FiMinusCircle, FiX } from "react-icons/fi";
import { BiStar } from "react-icons/bi";
import LogButton from "../../../../../../components/atoms/LogButton";
import { COLOR_GRAY_TEXT } from "../../../../../../constants/Colors";
import LogInput from "../../../../../../components/atoms/LogInput";
import LogTextArea from "../../../../../../components/atoms/LogTextArea";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  patchProviderProforma,
  patchTripDetails,
  postInvoice,
  postProviderProforma,
} from "../../../../../../api/modules";
import {
  errorToast,
  successToast,
} from "../../../../../../services/toastService";
import CurrencyFormat from "react-currency-format";
import LogDatePicker from "../../../../../../components/atoms/LogDatePicker";
import LogDateInput from "../../../../../../components/atoms/LogDateInput";

const ModalLogStyled = styled(Modal)`
  opacity: 1;
  .modal-content {
    border-radius: 15px !important;
    padding: 20px;
  }
  p {
    color: ${COLOR_GRAY_TEXT};
  }
`;

const InvoiceSchema = Yup.object().shape({
  /*  invoice_date: Yup.date()
    .required("Fecha de factura es obligatorio")
    .nullable(),
  invoice_number: Yup.string().required("Numero de factura es obligatorio"), */
});

const DocumentContainer = styled(Row)`
  max-height: 200px;
  overflow: auto;
`;

const ContentModal = styled.div`
  text-align: center;
`;

const ItemsPreviewContainer = styled.div`
  max-height: 260px;
  overflow: auto;

  textarea {
    min-height: 54px;
    resize: vertical;
  }

  .form-control-sm {
    font-size: 0.82rem;
  }
`;

const TomadoresList = styled.div`
  display: inline;
  margin-top: 2px;
`;

const TomadorItem = styled.small`
  color: ${COLOR_GRAY_TEXT};
  display: inline;
  line-height: 1.35;
`;

const TomadorCuit = styled.span`
  color: #343a40;
  font-weight: 600;
  margin-right: 6px;
  white-space: nowrap;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #000;
  &:hover {
    color: #ff0000;
  }
`;

const FACTURANTE_MEASURE_UNITS = [
  { value: 1, label: "unidades" },
  { value: 2, label: "kilogramos" },
  { value: 19, label: "toneladas" },
  { value: 44, label: "otras unidades" },
];

const PROFORMA_MEASURE_UNITS = [
  ...FACTURANTE_MEASURE_UNITS,
  { value: 1001, label: "km" },
  { value: 1002, label: "kilómetros" },
];

const FACTURANTE_VATS = [
  { value: 1, label: "NG", rate: 0 },
  { value: 2, label: "Exento", rate: 0 },
  { value: 3, label: "0%", rate: 0 },
  { value: 4, label: "2.5%", rate: 2.5 },
  { value: 5, label: "5%", rate: 5 },
  { value: 6, label: "10.5%", rate: 10.5 },
  { value: 7, label: "21%", rate: 21 },
  { value: 8, label: "27%", rate: 27 },
];

const MIPYME_TRANSFER_TYPES = [
  { value: "SCA", label: "SCA" },
  { value: "ADC", label: "ADC" },
];

const FACTURANTE_TRIBUTES = [
  { value: 10, label: "Percepción IIBB Tucumán" },
  { value: 6, label: "Otros tributos" },
  { value: 2, label: "Percepción Nacional IVA" },
  { value: 3, label: "Percepción Nacional Ganancias" },
  { value: 4, label: "Percepción IIBB AGIP" },
  { value: 5, label: "Percepción IIBB ARBA" },
  { value: 7, label: "Percepción IIBB Catamarca" },
  { value: 8, label: "Percepción IIBB Córdoba" },
  { value: 9, label: "Percepción IIBB Corrientes" },
  { value: 11, label: "Percepción IIBB Jujuy" },
  { value: 12, label: "Percepción IIBB Salta" },
  { value: 13, label: "Percepción IIBB Chaco" },
  { value: 14, label: "Percepción IIBB Misiones" },
  { value: 15, label: "Percepción IIBB Formosa" },
  { value: 16, label: "Percepción IIBB Entre Ríos" },
  { value: 17, label: "Percepción IIBB Santa Fé" },
  { value: 18, label: "Percepción IIBB Santiago del Estero" },
  { value: 19, label: "Percepción IIBB San Juan" },
  { value: 20, label: "Percepción IIBB Mendoza" },
  { value: 21, label: "Percepción IIBB Neuquén" },
  { value: 22, label: "Percepción IIBB La Pampa" },
  { value: 23, label: "Percepción IIBB Río Negro" },
  { value: 24, label: "Percepción IIBB Santa Cruz" },
  { value: 25, label: "Percepción IIBB Tierra del Fuego" },
  { value: 26, label: "Percepción IIBB San Luis" },
  { value: 27, label: "Percepción IIBB La Rioja" },
  { value: 28, label: "Percepción IIBB Chubut" },
  { value: 29, label: "Impuestos internos" },
];

const getMeasureUnitLabel = (
  measureUnit,
  measureUnits = FACTURANTE_MEASURE_UNITS
) =>
  measureUnits.find((unit) => unit.value === Number(measureUnit))
    ?.label || "unidades";

const getVatRate = (vat) =>
  FACTURANTE_VATS.find((item) => item.value === Number(vat))?.rate ?? 21;

const roundNumber = (value, decimals = 2) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;

  const factor = 10 ** decimals;
  return Math.round((parsed + Number.EPSILON) * factor) / factor;
};

const parseDecimalInput = (value) => {
  if (typeof value === "number") return value;
  if (value === null || value === undefined) return NaN;

  const trimmedValue = String(value).trim();
  if (!trimmedValue) return NaN;

  const normalizedValue = trimmedValue.includes(",")
    ? trimmedValue.replace(/\./g, "").replace(",", ".")
    : trimmedValue;

  return Number(normalizedValue);
};

const getFiniteDecimal = (value, fallback = 0) => {
  const parsed = parseDecimalInput(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getTripCode = (row) => `${row.parent_id}${row.order}`;

const getBaseAmount = (row) =>
  (Number(row.auditedWeight) || 0) * (Number(row.price_per_ton) || 0);

const getDadorCommissionAmount = (row) => {
  if (typeof row.comision_dador_pesos === "number") {
    return row.comision_dador_pesos;
  }

  const base = getBaseAmount(row);

  if (typeof row.discount_trip === "number") {
    return (base * (row.discount_trip / 2)) / 100;
  }

  return (base * (row.discount_dador || 0)) / 100;
};

const getTomadorCommissionAmount = (row) => {
  if (typeof row.comision_tomador_pesos === "number") {
    return row.comision_tomador_pesos;
  }

  const base = getBaseAmount(row);

  if (typeof row.discount_trip === "number") {
    return (base * (row.discount_trip / 2)) / 100;
  }

  return (base * (row.discount_tomador || 0)) / 100;
};

const getProformaUnitFromRow = (row) => {
  const unit = (row.unit_truck_charge || "").toLowerCase();

  if (unit.includes("ton")) {
    return {
      measure_unit: 19,
      measure_unit_label: getMeasureUnitLabel(19, PROFORMA_MEASURE_UNITS),
      quantity: roundNumber(Number(row.auditedWeight) || 0, 2).toFixed(2),
    };
  }

  if (unit.includes("km") || unit.includes("kilomet")) {
    return {
      measure_unit: 1001,
      measure_unit_label: getMeasureUnitLabel(1001, PROFORMA_MEASURE_UNITS),
      quantity: roundNumber(Number(row.id_trip?.kilometers) || 1, 2),
    };
  }

  if (unit.includes("kg") || unit.includes("kilo")) {
    return {
      measure_unit: 2,
      measure_unit_label: getMeasureUnitLabel(2, PROFORMA_MEASURE_UNITS),
      quantity: roundNumber((Number(row.auditedWeight) || 0) * 1000, 0),
    };
  }

  return {
    measure_unit: 1,
    measure_unit_label: getMeasureUnitLabel(1, PROFORMA_MEASURE_UNITS),
    quantity: 1,
  };
};

const buildDefaultCustomItems = (
  data = [],
  { isLpGeneration, isTomadorCommission, isProviderProforma }
) =>
  data.map((row) => {
    const code = getTripCode(row);
    const auditedWeight = Number(row.auditedWeight) || 0;
    const baseAmount = getBaseAmount(row);
    const itemTotal = isProviderProforma
      ? baseAmount - getTomadorCommissionAmount(row)
      : isTomadorCommission
      ? getTomadorCommissionAmount(row)
      : baseAmount - getDadorCommissionAmount(row);
    const commonDescriptionLines = [
      `Remito / CP / CTG: ${row.shipping_note || "-"}`,
    ];

    if (row.id_trip?.origin_site?.address) {
      commonDescriptionLines.push(
        `Origen: ${row.id_trip.origin_site.address}`
      );
    }

    if (row.id_trip?.destiny_site?.address) {
      commonDescriptionLines.push(
        `Destino: ${row.id_trip.destiny_site.address}`
      );
    }

    const commonDescription = commonDescriptionLines.join("\n");

    if (isProviderProforma) {
      const proformaUnit = getProformaUnitFromRow(row);
      const quantity = getFiniteDecimal(proformaUnit.quantity, 1);

      return {
        code: `PROF${code}`,
        item_type: "transport",
        trip_detail_id: row._id,
        shipping_note: row.shipping_note || "",
        description: `Servicio de transporte (Viaje ID${code})\n${commonDescription}`,
        quantity: proformaUnit.quantity,
        measure_unit: proformaUnit.measure_unit,
        measure_unit_label: proformaUnit.measure_unit_label,
        vat: 7,
        unit_price: roundNumber(quantity > 0 ? itemTotal / quantity : itemTotal, 4),
        source_quantity_ton: auditedWeight,
        source_quantity_km: Number(row.id_trip?.kilometers) || 0,
        source_net_total: itemTotal,
      };
    }

    if (isTomadorCommission) {
      return {
        code: `TCOM${code}`,
        description: `Servicio de Gestión Buena Ruta (Viaje ID${code})\n${commonDescription}`,
        quantity: 1,
        measure_unit: 1,
        measure_unit_label: getMeasureUnitLabel(1),
        vat: 7,
        unit_price: roundNumber(itemTotal, 2),
        source_quantity_ton: auditedWeight,
        source_net_total: itemTotal,
      };
    }

    return {
      code: isLpGeneration ? `LP${code}` : code,
      description: `${
        isLpGeneration
          ? "Servicio de Transporte de carga neto"
          : "Servicio de Transporte"
      } (Viaje ID${code})\n${commonDescription}`,
      quantity: 1,
      measure_unit: 1,
      measure_unit_label: getMeasureUnitLabel(1),
      vat: 7,
      unit_price: roundNumber(itemTotal, 2),
      source_quantity_ton: auditedWeight,
      source_net_total: itemTotal,
    };
  });

const getItemsTotal = (items = []) =>
  items.reduce((sum, item) => {
    const quantity = getFiniteDecimal(item.quantity);
    const unitPrice = getFiniteDecimal(item.unit_price);
    const vatMultiplier = 1 + getVatRate(item.vat) / 100;
    return sum + quantity * unitPrice * vatMultiplier * (item.item_type === "merma" ? -1 : 1);
  }, 0);

const getDefaultTributeAmountAffected = (data = []) => {
  const invoiceAmount = Number(data?.[0]?.invoice_id?.amount);
  if (Number.isFinite(invoiceAmount) && invoiceAmount > 0) {
    return roundNumber(invoiceAmount, 2);
  }

  return roundNumber(
    data.reduce((sum, row) => {
      const rowTotal = Number(row.total);
      if (Number.isFinite(rowTotal) && rowTotal > 0) return sum + rowTotal;

      return sum + getBaseAmount(row) * 1.21;
    }, 0),
    2
  );
};

const normalizeComparableItems = (items = [], isProviderProforma = false) =>
  items.map((item) => ({
    code: item.code || "",
    description: item.description?.trim() || "",
    quantity: parseDecimalInput(item.quantity),
    measure_unit: Number(item.measure_unit || 1),
    unit_price: parseDecimalInput(item.unit_price) *
      (isProviderProforma && item.item_type === "merma" ? -1 : 1),
    vat: Number(item.vat ?? 7),
    ...(isProviderProforma ? {
      item_type: item.item_type || "transport",
      trip_detail_id: item.trip_detail_id,
      source_quantity_ton: Number(item.source_quantity_ton) || 0,
      source_quantity_km: Number(item.source_quantity_km) || 0,
      source_net_total: Number(item.source_net_total) || 0,
    } : {}),
  }));

const normalizeProviderProformaItems = (items = [], trips = []) =>
  items.map((item, index) => {
    const trip = trips.find((row) => String(row._id) === String(item.trip_detail_id) ||
      item.code === `PROF${getTripCode(row)}`);
    return {
      code: item.code || `PROFITEM${index + 1}`,
      description: item.description || "",
      quantity: item.quantity ?? 1,
      measure_unit: Number(item.measure_unit || 1),
      measure_unit_label:
        item.measure_unit_label ||
        getMeasureUnitLabel(Number(item.measure_unit || 1), PROFORMA_MEASURE_UNITS),
      vat: Number(item.vat ?? 7),
      unit_price: item.item_type === "merma"
        ? Math.abs(Number(item.unit_price)) : item.unit_price ?? "",
      item_type: item.item_type || "transport",
      trip_detail_id: item.trip_detail_id || trip?._id,
      shipping_note: item.shipping_note ?? trip?.shipping_note ?? "",
      source_quantity_ton: Number(item.source_quantity_ton) || 0,
      source_quantity_km: Number(item.source_quantity_km) || 0,
      source_net_total: Number(item.source_net_total) || 0,
    };
  });

const getItemByMeasureUnit = (
  item,
  measureUnit,
  measureUnits = FACTURANTE_MEASURE_UNITS
) => {
  const unit = Number(measureUnit);
  const sourceTotal =
    Number(item.source_net_total) ||
    getFiniteDecimal(item.quantity) * getFiniteDecimal(item.unit_price);
  const sourceTons = Number(item.source_quantity_ton) || 0;
  const sourceKilometers = Number(item.source_quantity_km) || 0;

  let quantity = 1;
  let quantityDecimals = 2;
  let unitPriceDecimals = 2;

  if (unit === 19 && sourceTons > 0) {
    quantity = sourceTons;
  }

  if (unit === 2 && sourceTons > 0) {
    quantity = sourceTons * 1000;
    quantityDecimals = 0;
    unitPriceDecimals = 4;
  }

  if ((unit === 1001 || unit === 1002) && sourceKilometers > 0) {
    quantity = sourceKilometers;
    quantityDecimals = 2;
    unitPriceDecimals = 4;
  }

  const unitPrice = quantity > 0 ? sourceTotal / quantity : sourceTotal;

  return {
    ...item,
    measure_unit: unit,
    measure_unit_label: getMeasureUnitLabel(unit, measureUnits),
    quantity:
      unit === 19
        ? roundNumber(quantity, quantityDecimals).toFixed(quantityDecimals)
        : roundNumber(quantity, quantityDecimals),
    unit_price: roundNumber(unitPrice, unitPriceDecimals),
  };
};

const buildEmptyCustomItem = (
  items = [],
  measureUnits = FACTURANTE_MEASURE_UNITS
) => ({
  code: `ITEM${items.length + 1}`,
  item_type: "custom",
  description: "",
  quantity: 1,
  measure_unit: 1,
  measure_unit_label: getMeasureUnitLabel(1, measureUnits),
  vat: 1,
  unit_price: "",
  source_quantity_ton: 0,
  source_net_total: 0,
});

const ModalInvoice = ({
  isShow,
  handleClose,
  data,
  refetch,
  tryAgain = false,
  actionLabel,
  providerProforma,
}) => {
  const isLpGeneration = tryAgain && actionLabel === "Generar LP";
  const isTomadorCommission =
    tryAgain && actionLabel === "Generar comisión tomador";
  const isProviderProformaEdit = tryAgain && actionLabel === "Editar proforma";
  const isProviderProforma =
    tryAgain && (actionLabel === "Generar proforma" || isProviderProformaEdit);
  const mermaLocked = Boolean(isProviderProformaEdit && (
    providerProforma?.provider_invoice_status === "received" ||
    providerProforma?.provider_invoice_number || providerProforma?.provider_invoice_pdf_url
  ));
  const measureUnits = isProviderProforma
    ? PROFORMA_MEASURE_UNITS
    : FACTURANTE_MEASURE_UNITS;
  const defaultCustomItems = useMemo(
    () => {
      if (isProviderProformaEdit && providerProforma?.custom_items?.length) {
        return normalizeProviderProformaItems(providerProforma.custom_items, data);
      }

      return buildDefaultCustomItems(data, {
        isLpGeneration,
        isTomadorCommission,
        isProviderProforma,
      });
    },
    [
      data,
      isLpGeneration,
      isTomadorCommission,
      isProviderProforma,
      isProviderProformaEdit,
      providerProforma,
    ]
  );
  const defaultTributeAmountAffected = useMemo(
    () => getDefaultTributeAmountAffected(data),
    [data]
  );
  const tomadores = useMemo(() => {
    const tomadoresById = new Map();

    (data || []).forEach((row) => {
      const tomador = row.id_tomador;
      if (!tomador?._id || tomadoresById.has(tomador._id)) return;
      tomadoresById.set(tomador._id, tomador);
    });

    return [...tomadoresById.values()].sort((a, b) => {
      const aLabel = a.reason || a.email || a.cuit || "";
      const bLabel = b.reason || b.email || b.cuit || "";
      return aLabel.localeCompare(bLabel);
    });
  }, [data]);
  const hasMultipleTomadores = !tryAgain && tomadores.length > 1;

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: {
      invoice_number: "",
      invoice_date:
        isProviderProformaEdit && providerProforma?.date
          ? new Date(providerProforma.date)
          : new Date(),
      billing_flow_mode: "compraventa",
      facturante_document_type: 1,
      mipyme_cbu: "",
      mipyme_transfer_type: "SCA",
      invoice_observation: "",
      omit_invoice_comments: false,
      lp_adjustment_concept: "",
      lp_adjustment_amount: "",
      custom_items: defaultCustomItems,
      tribute_id: 10,
      tribute_amount_affected: defaultTributeAmountAffected,
      tribute_amount: "",
      tribute_comment: "",
    },
    onSubmit: async (values) => {
      const {
        invoice_number,
        invoice_date,
        billing_flow_mode,
        facturante_document_type,
        mipyme_cbu,
        mipyme_transfer_type,
        invoice_observation,
        omit_invoice_comments,
        lp_adjustment_concept,
        lp_adjustment_amount,
        custom_items,
        tribute_id,
        tribute_amount_affected,
        tribute_amount,
        tribute_comment,
      } = values;
      const hasLpAdjustment =
        isLpGeneration &&
        lp_adjustment_concept?.trim() &&
        lp_adjustment_amount !== "";
      const normalizedCustomItems = normalizeComparableItems(custom_items, isProviderProforma);
      const customItemsChanged =
        JSON.stringify(normalizedCustomItems) !==
        JSON.stringify(normalizeComparableItems(defaultCustomItems, isProviderProforma));
      const hasTribute =
        isTomadorCommission &&
        (tribute_amount !== "" || tribute_comment?.trim());
      const normalizedTributeAmount = parseDecimalInput(tribute_amount);
      const normalizedTributeAmountAffected = parseDecimalInput(
        tribute_amount_affected
      );
      const resp = isProviderProformaEdit
        ? await patchProviderProforma(providerProforma?._id, {
            proforma_date: invoice_date,
            custom_items: normalizedCustomItems,
          })
        : isProviderProforma
        ? await postProviderProforma({
            invoice_id: data[0]?.invoice_id?._id,
            proforma_date: invoice_date,
            ...(customItemsChanged
              ? { custom_items: normalizedCustomItems }
              : {}),
            trip_details: data.map((ele) => ele._id),
          })
        : await postInvoice({
        amount: data.reduce((total, row) => {
          const amount = row.auditedWeight * row.price_per_ton;
          return total + amount;
        }, 0),
        id_dador: data[0]?.id_dador._id,
        id_tomador: data[0]?.id_tomador._id,
        cuit: data[0]?.id_dador.cuit,
        reason: data[0]?.id_dador.reason,
        tomador_cuit: data[0]?.id_tomador.cuit,
        tomador_reason: data[0]?.id_tomador.reason,
        invoice_number,
        invoice_date,
        ...(!tryAgain ? { billing_flow_mode } : {}),
        ...(!tryAgain
          ? { facturante_document_type: Number(facturante_document_type) }
          : {}),
        ...(!tryAgain && Number(facturante_document_type) === 8
          ? {
              mipyme_cbu: String(mipyme_cbu || "").replace(/\D/g, ""),
              mipyme_transfer_type,
            }
          : {}),
        ...(!tryAgain && omit_invoice_comments
          ? { omit_invoice_comments: true }
          : {}),
        ...(!tryAgain && !omit_invoice_comments && invoice_observation?.trim()
          ? { invoice_observation: invoice_observation.trim() }
          : {}),
        ...(hasLpAdjustment
          ? {
              lp_adjustment_concept: lp_adjustment_concept.trim(),
              lp_adjustment_amount: Number(lp_adjustment_amount),
            }
          : {}),
        ...(customItemsChanged ? { custom_items: normalizedCustomItems } : {}),
        ...(hasTribute
          ? {
              tributes: [
                {
                  tribute_id: Number(tribute_id),
                  amount: normalizedTributeAmount,
                  amount_affected: normalizedTributeAmountAffected,
                  amount_type: 1,
                  ...(tribute_comment?.trim()
                    ? { comment: tribute_comment.trim() }
                    : {}),
                },
              ],
            }
          : {}),
        trip_details: data.map((ele) => ele._id),
      });
      const { msg } = resp;
      if (msg) {
        errorToast(msg || "Intente Nuevamente");
        /* resetForm(); */
      } else {
        successToast(
          isProviderProformaEdit
            ? "Proforma actualizada con exito"
            : isProviderProforma
            ? "Proforma generada con exito"
            : tryAgain
            ? `${actionLabel || "Operacion"} solicitada con exito`
            : "Factura A solicitada con exito"
        );
        refetch();
        resetForm();
        handleClose();
      }
    },
    validate: (values) => {
      const validationErrors = {};

      if (!tryAgain && Number(values.facturante_document_type) === 8) {
        const cbu = String(values.mipyme_cbu || "").replace(/\D/g, "");

        if (!/^\d{22}$/.test(cbu)) {
          validationErrors.mipyme_cbu = "CBU obligatorio de 22 dígitos";
        }

        if (
          !MIPYME_TRANSFER_TYPES.some(
            (type) => type.value === values.mipyme_transfer_type
          )
        ) {
          validationErrors.mipyme_transfer_type = "Tipo inválido";
        }
      }

      if (isLpGeneration) {
        const concept = values.lp_adjustment_concept?.trim();
        const amount = values.lp_adjustment_amount;

        if ((concept && !amount) || (!concept && amount)) {
          validationErrors.lp_adjustment_concept = "Informe concepto y monto";
          validationErrors.lp_adjustment_amount = "Informe concepto y monto";
        }

        if (
          amount &&
          (!Number.isFinite(Number(amount)) || Number(amount) <= 0)
        ) {
          validationErrors.lp_adjustment_amount = "Debe ser mayor a cero";
        }
      }

      if (isTomadorCommission) {
        const hasTribute =
          values.tribute_amount !== "" || values.tribute_comment?.trim();

        if (hasTribute) {
          const tributeAmount = parseDecimalInput(values.tribute_amount);
          const tributeAmountAffected = parseDecimalInput(
            values.tribute_amount_affected
          );
          const tributeId = Number(values.tribute_id);

          if (
            !FACTURANTE_TRIBUTES.some((tribute) => tribute.value === tributeId)
          ) {
            validationErrors.tribute_id = "Tipo inválido";
          }

          if (!Number.isFinite(tributeAmount) || tributeAmount <= 0) {
            validationErrors.tribute_amount = "Debe ser mayor a cero";
          }

          if (
            !Number.isFinite(tributeAmountAffected) ||
            tributeAmountAffected <= 0
          ) {
            validationErrors.tribute_amount_affected =
              "Debe ser mayor a cero";
          }
        }
      }

      values.custom_items?.forEach((item, index) => {
        const description = item.description?.trim();
        const quantity = parseDecimalInput(item.quantity);
        const unitPrice = parseDecimalInput(item.unit_price);
        const measureUnit = Number(item.measure_unit);
        const vat = Number(item.vat ?? 7);

        if (!description) {
          validationErrors[`custom_items.${index}.description`] =
            "Descripción obligatoria";
        }

        if (!Number.isFinite(quantity) || quantity <= 0) {
          validationErrors[`custom_items.${index}.quantity`] =
            "Cantidad inválida";
        }

        if (!Number.isFinite(unitPrice) || unitPrice < 0 ||
          (isProviderProforma && item.item_type === "merma" && unitPrice === 0)) {
          validationErrors[`custom_items.${index}.unit_price`] =
            "Precio inválido";
        }

        if (
          !measureUnits.some(
            (unit) => unit.value === measureUnit
          )
        ) {
          validationErrors[`custom_items.${index}.measure_unit`] =
            "Unidad inválida";
        }

        if (!FACTURANTE_VATS.some((itemVat) => itemVat.value === vat)) {
          validationErrors[`custom_items.${index}.vat`] = "IVA inválido";
        }
      });

      if (isProviderProforma) {
        const total = getItemsTotal(values.custom_items);
        const paid = (providerProforma?.paymentOrder_id || [])
          .reduce((sum, order) => sum + (Number(order.amount) || 0), 0);
        if (!Number.isFinite(total) || Math.round(total * 100) <= 0) {
          validationErrors.custom_items_total = "El total de la proforma debe ser mayor a cero.";
        } else if (Math.round(total * 100) < Math.round(paid * 100)) {
          validationErrors.custom_items_total = "El total no puede ser menor a lo ya pagado. Resolver el ajuste manualmente.";
        }
      }

      return validationErrors;
    },
    validationSchema: InvoiceSchema,
  });

  const updateCustomItem = (index, key, value) => {
    const nextItems = [...(values.custom_items || [])];
    nextItems[index] = {
      ...nextItems[index],
      [key]: value,
    };
    setFieldValue("custom_items", nextItems);
  };

  const updateCustomItemMeasureUnit = (index, measureUnit) => {
    const nextItems = [...(values.custom_items || [])];
    if (nextItems[index].item_type === "merma") return;
    nextItems[index] = getItemByMeasureUnit(
      nextItems[index],
      measureUnit,
      measureUnits
    );
    setFieldValue("custom_items", nextItems);
  };

  const addCustomItem = () => {
    const currentItems = values.custom_items || [];
    setFieldValue("custom_items", [
      ...currentItems,
      buildEmptyCustomItem(currentItems, measureUnits),
    ]);
  };

  const removeCustomItem = (index) => {
    const currentItems = values.custom_items || [];
    if (currentItems.length <= 1) return;
    const removed = currentItems[index];
    const dependentMerma = (item) => removed.item_type !== "merma" &&
      removed.trip_detail_id && item.item_type === "merma" &&
      String(item.trip_detail_id) === String(removed.trip_detail_id);
    if (mermaLocked && (removed.item_type === "merma" || currentItems.some(dependentMerma))) return;

    setFieldValue(
      "custom_items",
      currentItems.filter((item, itemIndex) => itemIndex !== index && !dependentMerma(item))
    );
  };

  const addMerma = (index) => {
    const items = values.custom_items || [];
    const source = items[index];
    if (mermaLocked || !source.trip_detail_id || items.some((item) =>
      item.item_type === "merma" && String(item.trip_detail_id) === String(source.trip_detail_id))) return;
    const trip = data.find((row) => String(row._id) === String(source.trip_detail_id));
    const tripCode = trip ? getTripCode(trip) : source.code.replace(/^PROF/, "");
    const merma = {
      code: `MERMA${source.trip_detail_id}`,
      item_type: "merma",
      trip_detail_id: source.trip_detail_id,
      shipping_note: source.shipping_note || "",
      description: `Descuento por merma (Viaje ID${tripCode})\nRemito / CP / CTG: ${source.shipping_note || "-"}`,
      quantity: "",
      measure_unit: 2,
      measure_unit_label: "kilogramos",
      unit_price: "",
      vat: 7,
    };
    setFieldValue("custom_items", [...items.slice(0, index + 1), merma, ...items.slice(index + 1)]);
  };

  const normalizeQuantityOnBlur = (index) => {
    const item = values.custom_items?.[index];
    if (!item || Number(item.measure_unit) !== 19) return;

    const quantity = parseDecimalInput(item.quantity);
    if (!Number.isFinite(quantity)) return;

    updateCustomItem(index, "quantity", roundNumber(quantity, 2).toFixed(2));
  };

  return (
    <ModalLogStyled
      show={isShow}
      size="lg"
      onHide={() => {
        resetForm();
        handleClose();
      }}
      centered
      className="modal-exit"
    >
      <Modal.Header>
        <CloseButton
          onClick={() => {
            resetForm();
            handleClose();
          }}
        >
          <FiX />
        </CloseButton>
      </Modal.Header>
      <Modal.Body className="pt-0">
        <Row>
          <Col className="text-left">
            <h4 className="font-weight-bold text-primary h5">
              {tryAgain
                ? actionLabel || "Avanzar facturación"
                : "Generar Factura A"}
            </h4>

            <hr />
          </Col>
        </Row>
        {tryAgain ? null : (
          <Row>
            <Col>
              <label className="black-primary font-weight-bold mr-2">
                Monto total Calculado:
              </label>
              <CurrencyFormat
                value={
                  data.reduce((total, row) => {
                    const amount = row.auditedWeight * row.price_per_ton;
                    return total + amount;
                  }, 0) || "No Informado"
                }
                displayType={"text"}
                thousandSeparator={true}
                decimalScale={2}
                fixedDecimalScale={true}
                prefix={"$"}
                renderText={(formattedValue) => <span>{formattedValue}</span>}
              />
            </Col>
          </Row>
        )}
        {!tryAgain ? (
          <Row className="mt-3">
            <Col>
              <label className="black-primary font-weight-bold mb-2">
                Forma de facturación:
              </label>
              <div className="d-flex flex-column flex-md-row">
                <Form.Check
                  className="mr-md-4 mb-2 mb-md-0 text-left"
                  id="billing-flow-cuenta-orden"
                  type="radio"
                  label="Cuenta y orden"
                  name="billing_flow_mode"
                  value="cuenta_orden"
                  checked={values.billing_flow_mode === "cuenta_orden"}
                  onChange={handleChange}
                />
                <Form.Check
                  className="text-left"
                  id="billing-flow-compraventa"
                  type="radio"
                  label="Compraventa"
                  name="billing_flow_mode"
                  value="compraventa"
                  checked={values.billing_flow_mode === "compraventa"}
                  onChange={handleChange}
                />
              </div>
              <small className="text-muted">
                {values.billing_flow_mode === "compraventa"
                  ? "Factura al dador sin leyenda cuenta y orden. No habilita LP."
                  : "Factura al dador por cuenta y orden. Luego habilita LP."}
              </small>
            </Col>
          </Row>
        ) : null}
        {!tryAgain ? (
          <Row className="mt-3">
            <Col>
              <label className="black-primary font-weight-bold mb-2">
                Tipo de comprobante:
              </label>
              <div className="d-flex flex-column flex-md-row">
                <Form.Check
                  className="mr-md-4 mb-2 mb-md-0 text-left"
                  id="facturante-document-type-a"
                  type="radio"
                  label="Factura A"
                  name="facturante_document_type"
                  value={1}
                  checked={Number(values.facturante_document_type) === 1}
                  onChange={handleChange}
                />
                <Form.Check
                  className="text-left"
                  id="facturante-document-type-mipyme-a"
                  type="radio"
                  label="MiPyMEs A"
                  name="facturante_document_type"
                  value={8}
                  checked={Number(values.facturante_document_type) === 8}
                  onChange={handleChange}
                />
              </div>
              <small className="text-muted">
                Usá MiPyMEs A cuando AFIP/Facturante lo exija para el CUIT del dador.
              </small>
            </Col>
          </Row>
        ) : null}
        {!tryAgain && Number(values.facturante_document_type) === 8 ? (
          <Row className="mt-3">
            <Col md={7} className="mb-2 mb-md-0">
              <label className="black-primary font-weight-bold mb-2">
                Datos MiPyME:
              </label>
              <LogInput
                type="text"
                inputMode="numeric"
                placeholder="CBU emisor (22 dígitos)"
                name="mipyme_cbu"
                value={values.mipyme_cbu}
                onChange={(event) => {
                  setFieldValue(
                    "mipyme_cbu",
                    event.target.value.replace(/\D/g, "")
                  );
                }}
                onBlur={handleBlur}
                errors={errors}
              />
            </Col>
            <Col md={5}>
              <label className="black-primary font-weight-bold mb-2">
                Transferencia:
              </label>
              <Form.Control
                as="select"
                name="mipyme_transfer_type"
                size="sm"
                value={values.mipyme_transfer_type}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                {MIPYME_TRANSFER_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Control>
              {errors.mipyme_transfer_type ? (
                <small className="text-danger">
                  {errors.mipyme_transfer_type}
                </small>
              ) : (
                <small className="text-muted">
                  SCA: circulación abierta. ADC: agente de depósito colectivo.
                </small>
              )}
            </Col>
          </Row>
        ) : null}
        <Row className="mt-3">
          <Col xs={4}>
            <label className="black-primary font-weight-bold mb-0 mt-2">
              Fecha comprobante:
            </label>
          </Col>
          <Col xs={8}>
            <LogDateInput
              size="md"
              name="invoice_date"
              className="number-font p-0"
              placeholder="Fecha de comprobante"
              onBlur={handleBlur.invoice_date}
              errors={errors}
              value={values.invoice_date}
              onChange={(value) => {
                setFieldValue("invoice_date", value);
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <label className="black-primary font-weight-bold mr-2">
              Cuit Dador:
            </label>
            <span>{data[0]?.id_dador.cuit} </span>
          </Col>
        </Row>
        {hasMultipleTomadores ? (
          <Row>
            <Col>
              <label className="black-primary font-weight-bold mr-2 mb-1">
                Tomadores:
              </label>
              <TomadoresList>
                {tomadores.map((tomador, index) => {
                  const name =
                    tomador.reason ||
                    [tomador.name, tomador.last_name].filter(Boolean).join(" ") ||
                    tomador.email;

                  return (
                    <TomadorItem key={tomador._id}>
                      {index > 0 ? " --- " : ""}
                      <TomadorCuit>{tomador.cuit}</TomadorCuit>
                      - {name}
                    </TomadorItem>
                  );
                })}
              </TomadoresList>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col>
              <label className="black-primary font-weight-bold mr-2">
                Cuit Tomador:
              </label>
              <span>{data[0]?.id_tomador.cuit} </span>
            </Col>
          </Row>
        )}
        {!tryAgain ? (
          <Row className="mt-3">
            <Col>
              <LogTextArea
                placeholder="Observación factura A"
                name="invoice_observation"
                value={values.invoice_observation}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={values.omit_invoice_comments}
                errors={errors}
              />
              <Form.Check
                className="mt-2 text-left"
                id="omit-invoice-comments"
                type="checkbox"
                label="Emitir sin observaciones"
                name="omit_invoice_comments"
                checked={values.omit_invoice_comments}
                onChange={(event) => {
                  setFieldValue("omit_invoice_comments", event.target.checked);
                  if (event.target.checked) {
                    setFieldValue("invoice_observation", "");
                  }
                }}
              />
            </Col>
          </Row>
        ) : null}
        {isLpGeneration ? (
          <Row className="mt-3">
            <Col md={7} className="mb-2 mb-md-0">
              <LogInput
                type="text"
                placeholder="Concepto adicional LP"
                name="lp_adjustment_concept"
                value={values.lp_adjustment_concept}
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
              />
            </Col>
            <Col md={5}>
              <LogInput
                type="number"
                min="0"
                step="0.01"
                placeholder="Monto a descontar"
                name="lp_adjustment_amount"
                value={values.lp_adjustment_amount}
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
              />
            </Col>
          </Row>
        ) : null}
        {isTomadorCommission ? (
          <Row className="mt-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="black-primary font-weight-bold mb-0">
                  Percepción / tributo
                </label>
                <small className="text-muted">
                  Opcional. Se suma a la factura de comisión tomador.
                </small>
              </div>
              <Row>
                <Col md={4} className="mb-2 mb-md-0">
                  <Form.Control
                    as="select"
                    name="tribute_id"
                    size="sm"
                    value={values.tribute_id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {FACTURANTE_TRIBUTES.map((tribute) => (
                      <option key={tribute.value} value={tribute.value}>
                        {tribute.label}
                      </option>
                    ))}
                  </Form.Control>
                  {errors.tribute_id ? (
                    <small className="text-danger">{errors.tribute_id}</small>
                  ) : null}
                </Col>
                <Col md={4} className="mb-2 mb-md-0">
                  <LogInput
                    type="text"
                    inputMode="decimal"
                    placeholder="Monto afectado"
                    name="tribute_amount_affected"
                    value={values.tribute_amount_affected}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                  />
                </Col>
                <Col md={4}>
                  <LogInput
                    type="text"
                    inputMode="decimal"
                    placeholder="Monto percepción"
                    name="tribute_amount"
                    value={values.tribute_amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  <LogInput
                    type="text"
                    placeholder="Comentario opcional"
                    name="tribute_comment"
                    value={values.tribute_comment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        ) : null}
        <Row className="mt-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="black-primary font-weight-bold mb-0">
                {isProviderProforma ? "Items de proforma" : "Items a facturar"}
              </label>
              <small className="text-muted">
                {isProviderProforma
                  ? "Se imprimen en la proforma. Podes ajustar unidad, cantidad y precio."
                  : "Si no modificas nada, se usa el detalle automatico."}
              </small>
            </div>
            <ItemsPreviewContainer>
              <Table bordered size="sm" responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Descripcion</th>
                    <th className="text-center">Cantidad</th>
                    <th className="text-center">Unidad</th>
                    <th className="text-center">Precio unit.</th>
                    <th className="text-center">IVA</th>
                    <th className="text-right">Total</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {values.custom_items?.map((item, index) => {
                    const isMerma = isProviderProforma && item.item_type === "merma";
                    const hasMerma = isProviderProforma && item.trip_detail_id &&
                      values.custom_items.some((other) => other.item_type === "merma" &&
                        String(other.trip_detail_id) === String(item.trip_detail_id));
                    const itemTotal =
                      getFiniteDecimal(item.quantity) *
                      getFiniteDecimal(item.unit_price) *
                      (1 + getVatRate(item.vat) / 100) * (isMerma ? -1 : 1);

                    return (
                      <tr key={`${item.code}-${index}`}>
                        <td style={{ minWidth: 260 }}>
                          <Form.Control
                            as="textarea"
                            size="sm"
                            value={item.description}
                            aria-label={isMerma ? "Detalle de merma" : "Detalle del item"}
                            disabled={isMerma && mermaLocked}
                            onChange={(event) =>
                              updateCustomItem(
                                index,
                                "description",
                                event.target.value
                              )
                            }
                          />
                          {errors[`custom_items.${index}.description`] ? (
                            <small className="text-danger">
                              {errors[`custom_items.${index}.description`]}
                            </small>
                          ) : null}
                          {isProviderProforma && !isMerma && item.trip_detail_id ? (
                            <button
                              type="button"
                              className="btn btn-link p-0 mt-1"
                              style={{ fontSize: "0.82rem" }}
                              disabled={mermaLocked || hasMerma}
                              title={mermaLocked ? "Factura del proveedor recibida" :
                                hasMerma ? "Este viaje ya tiene una merma" : "Agregar merma de mercaderia"}
                              onClick={() => addMerma(index)}
                            >
                              <FiMinusCircle className="mr-1" /> Agregar merma
                            </button>
                          ) : null}
                          {isMerma ? <small className="text-muted d-block mt-1">
                            {mermaLocked ? "Factura recibida: merma bloqueada" : "Merma de mercaderia"}
                          </small> : null}
                        </td>
                        <td style={{ width: 95 }}>
                          <Form.Control
                            type="text"
                            inputMode="decimal"
                            size="sm"
                            value={item.quantity}
                            placeholder={isMerma ? "Kilos" : undefined}
                            aria-label={isMerma ? "Kilos faltantes" : "Cantidad"}
                            disabled={isMerma && mermaLocked}
                            onChange={(event) =>
                              updateCustomItem(
                                index,
                                "quantity",
                                event.target.value
                              )
                            }
                            onBlur={() => normalizeQuantityOnBlur(index)}
                          />
                          {errors[`custom_items.${index}.quantity`] ? (
                            <small className="text-danger">
                              {errors[`custom_items.${index}.quantity`]}
                            </small>
                          ) : null}
                        </td>
                        <td style={{ width: 110 }}>
                          <Form.Control
                            as="select"
                            size="sm"
                            value={item.measure_unit || 1}
                            aria-label={isMerma ? "Unidad de merma" : "Unidad del item"}
                            disabled={isMerma}
                            onChange={(event) =>
                              updateCustomItemMeasureUnit(
                                index,
                                event.target.value
                              )
                            }
                          >
                            {measureUnits.map((unit) => (
                              <option key={unit.value} value={unit.value}>
                                {unit.label}
                              </option>
                            ))}
                          </Form.Control>
                          <small className="text-muted">
                            {isMerma ? "Kilos faltantes" : Number(item.measure_unit) === 19
                              ? "Peso auditado"
                              : Number(item.measure_unit) === 2
                              ? "Peso auditado x 1000"
                              : Number(item.measure_unit) === 1001 ||
                                Number(item.measure_unit) === 1002
                              ? "Kilometros"
                              : "Total del item"}
                          </small>
                          {errors[`custom_items.${index}.measure_unit`] ? (
                            <small className="text-danger d-block">
                              {errors[`custom_items.${index}.measure_unit`]}
                            </small>
                          ) : null}
                        </td>
                        <td style={{ width: 135 }}>
                          <Form.Control
                            type="text"
                            inputMode="decimal"
                            size="sm"
                            value={item.unit_price}
                            placeholder={isMerma ? "Precio/kg" : undefined}
                            aria-label={isMerma ? "Precio neto por kilo" : "Precio unitario"}
                            disabled={isMerma && mermaLocked}
                            onChange={(event) =>
                              updateCustomItem(
                                index,
                                "unit_price",
                                event.target.value
                              )
                            }
                          />
                          {errors[`custom_items.${index}.unit_price`] ? (
                            <small className="text-danger">
                              {errors[`custom_items.${index}.unit_price`]}
                            </small>
                          ) : null}
                        </td>
                        <td style={{ width: 105 }}>
                          <Form.Control
                            as="select"
                            size="sm"
                            value={item.vat ?? 7}
                            aria-label={isMerma ? "IVA de merma" : "IVA del item"}
                            disabled={isMerma && mermaLocked}
                            onChange={(event) =>
                              updateCustomItem(
                                index,
                                "vat",
                                Number(event.target.value)
                              )
                            }
                          >
                            {FACTURANTE_VATS.map((vat) => (
                              <option key={vat.value} value={vat.value}>
                                {vat.label}
                              </option>
                            ))}
                          </Form.Control>
                          {errors[`custom_items.${index}.vat`] ? (
                            <small className="text-danger">
                              {errors[`custom_items.${index}.vat`]}
                            </small>
                          ) : null}
                        </td>
                        <td className="text-right" style={{ width: 120 }}>
                          <CurrencyFormat
                            value={itemTotal}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"$"}
                            renderText={(formattedValue) => (
                              <span className={isMerma ? "text-danger" : undefined}>{formattedValue}</span>
                            )}
                          />
                        </td>
                        <td className="text-center" style={{ width: 42 }}>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-muted"
                            title="Eliminar item"
                            aria-label="Eliminar item"
                            disabled={(values.custom_items || []).length <= 1 ||
                              (mermaLocked && (isMerma || hasMerma))}
                            onClick={() => removeCustomItem(index)}
                          >
                            <FiX />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </ItemsPreviewContainer>
            {errors.custom_items_total ? (
              <small className="text-danger d-block mt-2">{errors.custom_items_total}</small>
            ) : null}
            <div className="d-flex justify-content-between align-items-center mt-2">
              <button
                type="button"
                className="btn btn-link p-0 font-weight-bold"
                onClick={addCustomItem}
              >
                Agregar item
              </button>
              <div className="d-flex align-items-center">
                <label className="black-primary font-weight-bold mr-2 mb-0">
                  Total items:
                </label>
                <CurrencyFormat
                  value={getItemsTotal(values.custom_items)}
                  displayType={"text"}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  prefix={"$"}
                  renderText={(formattedValue) => (
                    <span>{formattedValue}</span>
                  )}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="footer-modal">
        <Col className="d-flex justify-content-center">
          <LogButton
            disabled={isSubmitting}
            className="w-50"
            color="primary"
            onClick={handleSubmit}
            title={
              isSubmitting
                ? "Guardando..."
                : isProviderProformaEdit
                ? "Guardar proforma"
                : actionLabel || "Generar"
            }
            border="curve"
          />
        </Col>
      </Modal.Footer>
    </ModalLogStyled>
  );
};

export default ModalInvoice;
